import { useEffect, useMemo, useRef, useState } from "react"
import nProgress from "nprogress"
import useProductUnitRepositories from "repositories/product-unit"

/**
 * 200726: picker produk multi-select utk koreksi stok — mirror ProductPickerModal
 * form pembelian, tapi: sumber data /product/unit (bukan ps_list), TANPA kolom qty
 * (stok fisik / qty koreksi diisi di tabel setelah baris ditambah), plus kolom
 * stok outlet (batch /stock_list per halaman) dan penanda produk yang sudah ada
 * di daftar (koreksi stok menolak duplikat).
 *
 * Mount kondisional dari parent — hook fetch hanya hidup saat modal terbuka.
 */
export default function AdjustmentProductPickerModal({
  onClose,
  onAdd,
  outletId,
  existingIds = [],
  // 250726 (permintaan user): stok yg ditampilkan ikut satuan input yg dipilih di form
  // koreksi stok (PCS/LSN/PSG). Stok tersimpan PCS → dibagi faktor utk tampilan.
  uomLabel = "PCS",
  uomFactor = 1,
}) {
  const searchInput = useRef(null)

  // Format stok (PCS) → satuan terpilih (÷ faktor). "…" saat masih loading (undefined).
  const fmtStock = (pcs) => {
    if (pcs === undefined || pcs === null) return "…"
    const n = Number(pcs)
    if (!Number.isFinite(n)) return "…"
    if (uomFactor > 1) return `${Math.round((n / uomFactor) * 100) / 100} ${uomLabel}`
    return String(n)
  }

  const [searchValue, setSearchValue] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  // Map pid -> product — bertahan lintas halaman/pencarian.
  const [selected, setSelected] = useState(() => new Map())

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchValue.trim())
      setPage(1)
    }, 400)
    return () => clearTimeout(t)
  }, [searchValue])

  useEffect(() => {
    const t = setTimeout(() => searchInput.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [])

  const { data: raw, isLoading } = useProductUnitRepositories({
    paginate: true,
    page,
    search: search || undefined,
  })

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  const rows = useMemo(() => {
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw?.data)) return raw.data
    return []
  }, [raw])

  // Stok outlet per baris halaman ini — batch SATU panggilan /stock_list
  // (pola optionStocks di StockAdjustmentContent, bukan N+1).
  const [pageStocks, setPageStocks] = useState({})
  useEffect(() => {
    const ids = rows.map((v) => String(v?.id ?? "")).filter(Boolean)
    if (!outletId || ids.length === 0) {
      setPageStocks({})
      return
    }
    let cancelled = false
    fetch(`${process.env.REACT_APP_INVENTORY_API_URL}/stock_list`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ product_unit_ids: ids, outlet_id: Number(outletId) }),
    })
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return
        const map = {}
        ;(Array.isArray(json?.data) ? json.data : []).forEach((row) => {
          const id = String(row?.product_unit_id ?? "")
          if (id) map[id] = Number(row?.current_stock ?? row?.stock ?? row?.balance ?? 0)
        })
        setPageStocks(map)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [rows, outletId])

  const existingSet = useMemo(
    () => new Set((existingIds || []).map((v) => String(v))),
    [existingIds]
  )

  const pidOf = (p) => String(p?.id ?? "")
  const labelOf = (p) => p?.long_name || p?.name || pidOf(p) || "-"
  const descOf = (p) => [p?.sku, p?.product_model?.name].filter(Boolean).join(" · ")

  const toggle = (p) => {
    const pid = pidOf(p)
    if (!pid || existingSet.has(pid)) return
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(pid)) next.delete(pid)
      else next.set(pid, p)
      return next
    })
  }

  const selectableRows = rows.filter((p) => !existingSet.has(pidOf(p)))
  const allPageSelected =
    selectableRows.length > 0 && selectableRows.every((p) => selected.has(pidOf(p)))

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (allPageSelected) {
        for (const p of selectableRows) next.delete(pidOf(p))
      } else {
        for (const p of selectableRows) {
          const pid = pidOf(p)
          if (pid && !next.has(pid)) next.set(pid, p)
        }
      }
      return next
    })
  }

  const hasNext = Boolean(raw?.next_page_url)
  const hasPrev = page > 1

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Pilih Produk</h3>
            <p className="text-sm text-neutral-500">
              Centang beberapa produk sekaligus — pilihan tetap tersimpan saat ganti halaman atau kata kunci.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          <input
            ref={searchInput}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="text"
            placeholder="Cari product unit / SKU..."
            className="w-full py-3 pl-4 pr-10 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
          />

          <div className="border rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 text-xs font-semibold border-b bg-neutral-50 text-neutral-600">
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={togglePage}
                  title="Pilih semua di halaman ini"
                />
              </div>
              <div className="col-span-8">Produk</div>
              <div className="col-span-3">{outletId ? "Stok Outlet" : ""}</div>
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Memuat produk...
                </div>
              ) : !rows.length ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Produk tidak ditemukan.
                </div>
              ) : (
                rows.map((p) => {
                  const pid = pidOf(p)
                  const already = existingSet.has(pid)
                  const picked = selected.has(pid)
                  return (
                    <div
                      key={pid}
                      className={`grid grid-cols-12 gap-3 items-center px-4 py-3 text-sm border-b last:border-b-0 ${
                        already
                          ? "opacity-50 cursor-not-allowed"
                          : picked
                            ? "bg-orange-50 cursor-pointer"
                            : "hover:bg-neutral-50 cursor-pointer"
                      }`}
                      onClick={() => toggle(p)}
                    >
                      <div className="col-span-1">
                        <input type="checkbox" checked={picked} disabled={already} readOnly />
                      </div>
                      <div className="col-span-8 min-w-0">
                        <div className="font-medium text-neutral-900 truncate">{labelOf(p)}</div>
                        <div className="text-xs text-neutral-500 truncate">{descOf(p)}</div>
                      </div>
                      <div className="col-span-3 text-xs text-neutral-600">
                        {already
                          ? "Sudah di daftar"
                          : outletId
                            ? `Stok: ${fmtStock(pageStocks[pid])}`
                            : ""}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-neutral-600">
              <button
                type="button"
                disabled={!hasPrev}
                onClick={() => setPage((x) => Math.max(1, x - 1))}
                className="px-3 py-1.5 border rounded-lg border-neutral-200 disabled:opacity-40"
              >
                ‹ Prev
              </button>
              <span>Hal {page}</span>
              <button
                type="button"
                disabled={!hasNext}
                onClick={() => setPage((x) => x + 1)}
                className="px-3 py-1.5 border rounded-lg border-neutral-200 disabled:opacity-40"
              >
                Next ›
              </button>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-neutral-600">
                <b>{selected.size}</b> produk dipilih
              </span>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected(new Map())}
                  className="text-xs text-neutral-500 underline"
                >
                  Bersihkan
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-xs border rounded-xl border-neutral-200 text-neutral-700"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={!selected.size}
                onClick={() => onAdd?.([...selected.values()])}
                className={`px-4 py-2.5 text-xs font-semibold text-white rounded-xl ${
                  selected.size ? "bg-brand-900 hover:bg-brand-800" : "bg-neutral-400 cursor-not-allowed"
                }`}
              >
                Tambah {selected.size || ""} Produk
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
